import {Component, OnDestroy, OnInit} from '@angular/core';
import {ListView, TypeComparaison, TypeFilter} from "@domain/common/list-view";
import {Fournisseur} from '@domain/facture/fournisseur';
import {FacturesFournisseursItemComponent} from "@components/admin/frais/factures/fournisseurs/fournisseurs-item/factures-fournisseurs-item.component";
import {TranslateService} from '@ngx-translate/core';
import {Router} from "@angular/router";
import {environment} from '@environments/environment';
import {Sorting} from '@domain/common/list-view/sorting';
import {BehaviorSubject} from "rxjs";
import {FloatingButtonAction, TypeAction} from '@share/component/floating-button/floating-button';
import {FraisFacturesService} from "@components/admin/frais/factures/frais-factures.service";

/**
 * Onglet factures fournisseurs
 *
 * @author Guillaume TRAVOT
 * @date 21/11/2023
 */
@Component({
    selector: 'factures-fournisseurs',
    host: {'data-test-id': 'factures-fournisseurs'},
    templateUrl: './factures-fournisseurs.component.html'
})
export class FacturesFournisseursComponent implements OnInit, OnDestroy {
    /** Liste view des fournisseurs */
    fournisseurListView: ListView<Fournisseur, FacturesFournisseursItemComponent>;

    /** Liste des actions disponibles */
    listeActions: BehaviorSubject<Array<FloatingButtonAction>> = new BehaviorSubject<Array<FloatingButtonAction>>(null);

    /**
     * Constructeur
     *
     * @param translateService Service de traduction
     * @param fraisFacturesService Service de gestion des frais factures
     * @param router Service de routage
     */
    constructor(
        public translateService: TranslateService,
        private fraisFacturesService: FraisFacturesService,
        private router: Router,
    ) {
    }

    /**
     * Initialisation
     */
    ngOnInit(): void {
        //Initialisation de la liste view
        this.fournisseurListView = new ListView<Fournisseur, FacturesFournisseursItemComponent>({
            component: FacturesFournisseursItemComponent,
            uri: `${environment.baseUrl}/controller/Facture/listeFournisseurs`,
            title: this.translateService.instant('admin.frais.fournisseurs.title'),
            defaultOrder: 'raisonSociale',
            listeFilters: [{
                clef: 'raisonSociale',
                title: this.translateService.instant('admin.frais.fournisseurs.filtres.raisonSociale'),
                type: TypeFilter[TypeFilter.STRING],
                typeComparaison: TypeComparaison[TypeComparaison.LIKE],
                isDefault: true
            }, {
                clef: 'codeFournisseur',
                title: this.translateService.instant('admin.frais.fournisseurs.code'),
                type: TypeFilter[TypeFilter.STRING],
                typeComparaison: TypeComparaison[TypeComparaison.LIKE],
                isDefault: true
            }, {
                clef: 'devise',
                title: this.translateService.instant('admin.frais.fournisseurs.devise'),
                type: TypeFilter[TypeFilter.STRING],
                typeComparaison: TypeComparaison[TypeComparaison.LIKE]
            }, {
                clef: 'orga.libelle',
                title: this.translateService.instant('admin.frais.fournisseurs.service'),
                type: TypeFilter[TypeFilter.STRING],
                typeComparaison: TypeComparaison[TypeComparaison.LIKE]
            }, {
                clef: 'isActif',
                title: this.translateService.instant('admin.frais.fournisseurs.actif'),
                type: TypeFilter[TypeFilter.BOOLEAN],
                typeComparaison: TypeComparaison[TypeComparaison.EQUAL]
            }],
            isFilter: true
        });

        //Définition des colonnes de tri
        this.fournisseurListView.columns = [
            {key: 'raisonSociale', title: this.translateService.instant('admin.frais.fournisseurs.filtres.raisonSociale')},
            {key: 'codeFournisseur', title: this.translateService.instant('admin.frais.fournisseurs.code')},
            {key: 'devise', title: this.translateService.instant('admin.frais.fournisseurs.devise')},
            {key: 'orga.libelle', title: this.translateService.instant('admin.frais.fournisseurs.service')},
            {key: 'isActif', title: this.translateService.instant('admin.frais.fournisseurs.actif')}
        ];

        //Ajout du tri de la liste selon l'ordre voulu
        this.fournisseurListView.sorting = new Sorting(
            this.fournisseurListView.columns,
            'raisonSociale'
        );

        //Nom de la classe
        this.fournisseurListView.className = "factures-fournisseurs";

        //Persistence des filtres
        this.fournisseurListView.isPersistFilters = true;

        //Définition de la liste des actions possibles
        this.listeActions.next([
            {
                type: TypeAction.PRIMARY,
                icone: 'nio icon-ajouter',
                libelle: 'admin.frais.fournisseurs.action.ajouter',
                doAction: () => this.creerFournisseur()
            }
        ]);
    }

    /**
     * Destruction
     */
    ngOnDestroy() {
        //Indique au composant Frais Factures qu'on n'affiche plus le détail d'un fournisseur
        this.fraisFacturesService.isShowDetail = false;
    }

    /**
     * Méthode appelée lors de la création d'un nouveau fournisseur
     */
    creerFournisseur(): void {
        this.router.navigate(['Admin/Frais/Factures/Fournisseurs/0']);
    }
}
